import * as React from "react";

import * as Redux from "react-redux";

import {
  AddMessagingStep,
  CashPayIntegrationStep,
  IntegrationSkeleton,
  IntegrationStep,
} from "client/domain";
import {
  useFeatureFlags,
  useMerchantModel,
  useOnboardingStatusContext,
} from "client/hooks";
import { RootState } from "client/store";

import { AddCashPayStep } from "./AddCashPayStep";
import { CashPayProfileStatusStep } from "./CashPayProfileStatusStep";
import { CustomizeCashPayProfileStep } from "./CustomizeCashPayProfileStep";

// Show Add Cash Pay Step if:
// merchantPortalReleaseCashPayFeatureSet2 AND merchantPortalReleaseCashPay feature flags are on
// AND merchant is eligible for Cash Pay
export const showAddCashPayStep = (
  merchantPortalReleaseCashPayFlag: boolean,
  merchantPortalReleaseCashPayFeatureSet2Flag: boolean,
  isEligibleForCashPay: boolean
): boolean =>
  merchantPortalReleaseCashPayFlag &&
  merchantPortalReleaseCashPayFeatureSet2Flag &&
  isEligibleForCashPay;

// Show Cash Pay Integration Steps if:
// merchantPortalReleaseCashPay feature flag is on
// AND Cash Pay Form has not yet been completed
// (merchant would have to pass eligibility checks to submit form)
export const showCashPayIntegrationSteps = (
  merchantPortalReleaseCashPayFlag: boolean,
  cashPayFormComplete: boolean
): boolean => cashPayFormComplete && merchantPortalReleaseCashPayFlag;

// Show Cash Pay Profile Status Card if:
// cash pay integration step has been complete
// AND cash_pay_profile_imate_url__c field is updated and not empty
// (either through cash pay form or customize profile card)
export const showCashPayProfileStatusStep = (
  cashPayIntegrationComplete: boolean,
  cashPayLogoUrl: string | undefined
): boolean => Boolean(cashPayLogoUrl) && cashPayIntegrationComplete;

export const OnlineStatusSteps: React.FC = () => {
  const {
    getApcCredentialsResponse,
    zdoEligibilityPolling,
    isEligibleForCashPay,
    cashPayIntegrationComplete,
  } = useOnboardingStatusContext();

  const { merchant } = useMerchantModel();
  const cashPayLogoUrl = merchant.cashPay.cashPayLogoUrl?.value;

  const [
    { merchantPortalReleaseCashPay, merchantPortalReleaseCashPayFeatureSet2 },
  ] = useFeatureFlags();

  const {
    formCompletion: { cashPayForm },
  } = Redux.useSelector(({ formCompletion }: RootState) => ({
    formCompletion,
  }));

  return (
    <>
      {zdoEligibilityPolling ? (
        <IntegrationSkeleton />
      ) : (
        <>
          <IntegrationStep
            credentials={getApcCredentialsResponse?.credentials}
          />
        </>
      )}
      {showAddCashPayStep(
        merchantPortalReleaseCashPay,
        merchantPortalReleaseCashPayFeatureSet2,
        isEligibleForCashPay
      ) && <AddCashPayStep />}
      {showCashPayIntegrationSteps(
        cashPayForm,
        merchantPortalReleaseCashPay
      ) && (
        <>
          <CashPayIntegrationStep
            credentials={getApcCredentialsResponse?.credentials}
          />
          {merchantPortalReleaseCashPayFeatureSet2 && (
            <CustomizeCashPayProfileStep />
          )}
          {showCashPayProfileStatusStep(
            cashPayIntegrationComplete,
            cashPayLogoUrl
          ) && (
            <CashPayProfileStatusStep
              merchantId={
                getApcCredentialsResponse?.credentials?.merchantId as string
              }
            />
          )}
        </>
      )}
      <AddMessagingStep />
    </>
  );
};
